import React, { useEffect, useMemo, useState } from 'react';
import './Damages.css';

export default function ManageDamages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // filters / UI
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('all');
  const [sev, setSev] = useState('all');
  const [sortBy, setSortBy] = useState('reported_at');

  // selection / bulk
  const [selected, setSel] = useState(new Set());
  const [assignee, setAsg] = useState('');
  const [drawer, setDrawer] = useState(null);

  useEffect(() => {
    try {
      setItems(seed());
    } catch (e) {
      console.error(e);
      setError('Could not load damages');
    } finally {
      setLoading(false);
    }
  }, []);

  // filter + sort
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((i) => {
      const matchQ =
        !ql ||
        i.id.toLowerCase().includes(ql) ||
        i.ride_id.toLowerCase().includes(ql) ||
        i.car.toLowerCase().includes(ql) ||
        i.valet.toLowerCase().includes(ql) ||
        i.notes.some((n) => n.text.toLowerCase().includes(ql));
      const matchStatus = status === 'all' ? true : i.status === status;
      const matchType   = type === 'all'   ? true : i.type === type;
      const matchSev    = sev === 'all'    ? true : i.severity === sev;
      return matchQ && matchStatus && matchType && matchSev;
    });
  }, [items, q, status, type, sev]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const sevRank = { high: 3, medium: 2, low: 1 };
    if (sortBy === 'reported_at') arr.sort((a, b) => new Date(b.reported_at) - new Date(a.reported_at));
    if (sortBy === 'severity')    arr.sort((a, b) => (sevRank[b.severity] ?? 0) - (sevRank[a.severity] ?? 0));
    if (sortBy === 'type')        arr.sort((a, b) => a.type.localeCompare(b.type));
    if (sortBy === 'status')      arr.sort((a, b) => a.status.localeCompare(b.status));
    return arr;
  }, [filtered, sortBy]);

  // selection helpers
  const allChecked  = sorted.length > 0 && sorted.every((i) => selected.has(i.id));
  const someChecked = !allChecked && [...selected].some((id) => sorted.find((i) => i.id === id));
  const toggleOne   = (id) => setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll   = () => setSel(allChecked ? new Set() : new Set(sorted.map((i) => i.id)));

  // optimistic bulk actions
  const mutateMany = (ids, updater) => setItems((prev) => prev.map((i) => (ids.includes(i.id) ? updater(i) : i)));
  const bulkAssign   = () => { const ids = [...selected]; mutateMany(ids, (i) => ({ ...i, assigned_to: assignee || 'Unassigned' })); setSel(new Set()); };
  const bulkResolve  = () => { const ids = [...selected]; mutateMany(ids, (i) => ({ ...i, status: 'resolved' })); setSel(new Set()); };
  const bulkEscalate = () => { const ids = [...selected]; mutateMany(ids, (i) => ({ ...i, status: 'escalated' })); setSel(new Set()); };
  const bulkDelete   = () => { const ids = [...selected]; setItems((prev) => prev.filter((i) => !ids.includes(i.id))); setSel(new Set()); };

  const addNote      = (id, text) => setItems((prev) => prev.map((i) => i.id === id ? { ...i, notes: [{ id: cryptoId(), at: new Date().toISOString(), text }, ...i.notes] } : i));
  const changeStatus = (id, s)    => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status: s } : i)));
  const assignOne    = (id, to)   => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, assigned_to: to } : i)));

  if (loading) return <p className="pad">Loading damages…</p>;
  if (error)   return <p className="pad error">{error}</p>;

  return (
    <div className="page">
      {/* KPIs */}
      <div className="kpi-grid">
        <Kpi title="Open"      value={items.filter((i) => i.status === 'open').length}       tag="Live"      tagClass="pill-ok" />
        <Kpi title="Escalated" value={items.filter((i) => i.status === 'escalated').length}  tag="Attention" tagClass="pill-warn" />
        <Kpi title="Resolved"  value={items.filter((i) => i.status === 'resolved').length}   tag="Done"      tagClass="pill-info" />
      </div>

      {/* Controls toolbar (horizontal, responsive) */}
      <div className="card controls">
        <input className="input search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ID, ride, car, valet, notes…" />

        <label className="ctl">Status</label>
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All</option><option value="open">Open</option>
          <option value="escalated">Escalated</option><option value="resolved">Resolved</option>
        </select>

        <label className="ctl">Type</label>
        <select className="select" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="all">All</option><option value="scratch">Scratch</option>
          <option value="dent">Dent</option><option value="broken_mirror">Broken mirror</option><option value="other">Other</option>
        </select>

        <label className="ctl">Severity</label>
        <select className="select" value={sev} onChange={(e) => setSev(e.target.value)}>
          <option value="all">All</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select>

        <span className="ctl label-strong">Sort:</span>
        <div className="btn-group">
          <button className={`btn ${sortBy === 'reported_at' ? 'btn-primary' : 'btn-chip'}`} onClick={() => setSortBy('reported_at')}>Newest</button>
          <button className={`btn ${sortBy === 'severity'    ? 'btn-primary' : 'btn-chip'}`} onClick={() => setSortBy('severity')}>Severity</button>
          <button className={`btn ${sortBy === 'type'        ? 'btn-primary' : 'btn-chip'}`} onClick={() => setSortBy('type')}>Type</button>
          <button className={`btn ${sortBy === 'status'      ? 'btn-primary' : 'btn-chip'}`} onClick={() => setSortBy('status')}>Status</button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="card bulk">
          <span className="muted">{selected.size} selected</span>
          <input className="input small" placeholder="Assign to…" value={assignee} onChange={(e) => setAsg(e.target.value)} />
          <div className="spacer" />
          <button className="btn btn-primary" disabled={!assignee} onClick={bulkAssign}>Assign</button>
          <button className="btn btn-warn"    onClick={bulkEscalate}>Escalate</button>
          <button className="btn btn-ok"      onClick={bulkResolve}>Resolve</button>
          <button className="btn btn-danger"  onClick={bulkDelete}>Delete</button>
        </div>
      )}

      {/* Table */}
      <div className="card">
        <div className="card-head"><h2>Manage Damages</h2></div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked; }}
                    onChange={toggleAll}
                  />
                </th>
                <th>Incident</th><th>Ride</th><th>Car</th><th>Valet</th>
                <th>Reported</th><th>Type</th><th>Severity</th><th>Photos</th>
                <th>Assigned</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((i) => (
                <tr key={i.id}>
                  <td><input type="checkbox" checked={selected.has(i.id)} onChange={() => toggleOne(i.id)} /></td>
                  <td><strong>{i.id}</strong></td>
                  <td>{i.ride_id}</td>
                  <td>{i.car}</td>
                  <td>{i.valet}</td>
                  <td>{i.reported_at}</td>
                  <td><span className={`pill pill-type-${i.type}`}>{labelForType(i.type)}</span></td>
                  <td><span className={`pill pill-${i.severity}`}>{i.severity}</span></td>
                  <td>{i.photos}</td>
                  <td>{i.assigned_to || '—'}</td>
                  <td>{i.status}</td>
                  <td className="row-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => setDrawer(i)}>View</button>
                    <button className="btn btn-chip btn-sm" onClick={() => promptNote(i, addNote)}>Add note</button>
                  </td>
                </tr>
              ))}
              {!sorted.length && (
                <tr><td colSpan="12" className="empty">No incidents match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer */}
      {drawer && (
        <div className="drawer-backdrop" onClick={() => setDrawer(null)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-head">
              <h3>{drawer.id} • {drawer.car}</h3>
              <button className="btn btn-chip" onClick={() => setDrawer(null)}>×</button>
            </div>
            <p className="muted">Ride {drawer.ride_id} • Reported {drawer.reported_at} by {drawer.valet}</p>

            <div className="drawer-body">
              <div className="drawer-col">
                <h4>Details</h4>
                <p><strong>Type:</strong> {labelForType(drawer.type)} • <strong>Severity:</strong> {drawer.severity}</p>
                <p><strong>Status:</strong> {drawer.status} • <strong>Assigned:</strong> {drawer.assigned_to || '—'}</p>

                <div className="inline-edit">
                  <InlineAssign
                    value={drawer.assigned_to}
                    onSave={(to) => { assignOne(drawer.id, to); setDrawer((d) => ({ ...d, assigned_to: to })); }}
                  />
                  <InlineStatus
                    value={drawer.status}
                    onSave={(s) => { changeStatus(drawer.id, s); setDrawer((d) => ({ ...d, status: s })); }}
                  />
                </div>

                <div className="drawer-quick">
                  <button className="btn btn-warn"   onClick={() => { changeStatus(drawer.id, 'escalated'); setDrawer((d) => ({ ...d, status: 'escalated' })); }}>Escalate</button>
                  <button className="btn btn-ok"     onClick={() => { changeStatus(drawer.id, 'resolved');  setDrawer((d) => ({ ...d, status: 'resolved' }));  }}>Resolve</button>
                </div>

                <h4>Notes</h4>
                <button className="btn btn-chip btn-sm" onClick={() => promptNote(drawer, addNote)}>Add note</button>
                <ul className="notes">
                  {drawer.notes.map((n) => (
                    <li key={n.id}><span className="muted small">{fmt(n.at)}:</span> {n.text}</li>
                  ))}
                </ul>
              </div>

              <div className="drawer-col photos">
                <h4>Photos ({drawer.photos})</h4>
                <div className="photo-grid">
                  {Array.from({ length: Math.max(1, drawer.photos) }).map((_, ix) => (
                    <img key={ix} src="https://placehold.co/240x160/jpg" alt="evidence" />
                  ))}
                </div>
                <label className="btn btn-primary btn-sm upload">
                  Upload
                  <input type="file" multiple onChange={(e) => {
                    const ct = (e.target.files || []).length;
                    setDrawer((d) => ({ ...d, photos: d.photos + ct }));
                  }} />
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== helpers ===== */
function promptNote(item, add) {
  const text = window.prompt(`Add note to ${item.id}`, '');
  if (!text) return;
  add(item.id, text);
}
function labelForType(t) {
  return ({ scratch: 'Scratch', dent: 'Dent', broken_mirror: 'Broken mirror', other: 'Other' }[t] || t);
}
function fmt(iso) { return new Date(iso).toLocaleString(); }
const cryptoId = () => Math.random().toString(36).slice(2, 8);

function seed() {
  return [
    mk('DMG-1027', 'R-88321', 'Tesla Model 3 • BSC-921', 'Sergio Ramos', 'scratch', 'low', 2, 'open', 'Rear left bumper hairline scratch.'),
    mk('DMG-1028', 'R-88344', 'BMW X5 • JKP-552', 'Amira Khan', 'dent', 'high', 4, 'escalated', 'Deep dent on passenger door. Customer notified.'),
    mk('DMG-1029', 'R-88377', 'Toyota Yaris • ABC-123', 'Leo Martins', 'broken_mirror', 'medium', 1, 'open', 'Mirror housing loose; crack visible.'),
    mk('DMG-1030', 'R-88390', 'Audi A6 • CCD-909', 'Mona Li', 'other', 'low', 0, 'resolved', 'Floor mat stain; cleaned on-site.'),
  ];
}
function mk(id, ride_id, car, valet, type, severity, photos, status, note) {
  return {
    id, ride_id, car, valet, type, severity, photos, status,
    reported_at: new Date(Date.now() - Math.random() * 2 * 864e5).toISOString().slice(0, 16).replace('T', ' '),
    assigned_to: '',
    notes: [{ id: 'n1', at: new Date().toISOString(), text: note }],
  };
}

/* ===== inline widgets (styled via CSS) ===== */
function InlineStatus({ value, onSave }) {
  const [v, setV] = useState(value);
  return (
    <div className="field-inline">
      <span className="muted small">Set status:</span>
      <select className="select" value={v} onChange={(e) => { setV(e.target.value); onSave(e.target.value); }}>
        <option value="open">Open</option>
        <option value="escalated">Escalated</option>
        <option value="resolved">Resolved</option>
      </select>
    </div>
  );
}
function InlineAssign({ value, onSave }) {
  const [v, setV] = useState(value || '');
  return (
    <div className="field-inline">
      <span className="muted small">Reassign to:</span>
      <input className="input small" value={v} onChange={(e) => setV(e.target.value)} placeholder="Name / email" />
      <button className="btn btn-primary btn-sm" onClick={() => onSave(v)} disabled={!v}>Save</button>
    </div>
  );
}

function Kpi({ title, value, tag, tagClass }) {
  return (
    <div className="kpi">
      <h3>{title}</h3>
      <p className="kpi-value">{value}</p>
      {tag && <span className={`pill ${tagClass}`}>{tag}</span>}
    </div>
  );
}